// Ekrany bramki logowania w AppShell (tylko przy włączonej fladze AUTH_REQUIRED):
// logowanie zamiast treści, wymuszona zmiana hasła tymczasowego, „brak dostępu",
// błąd sprawdzania sesji, okno ponownego logowania NAD stroną po 401 i dymek
// z komunikatem. Każdy w języku stron /konto i /admin (AuthUi).

import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { authApi, type Me } from '../../lib/auth'
import { LangSwitch } from '../LangSwitch'
import { LegiaHubWordmark } from '../LegiaHubWordmark'
import { TeamCrest } from '../TeamCrest'
import { BTN_PRIMARY, BTN_SECONDARY } from './AuthUi'
import { LoginForm } from './LoginForm'
import { PasswordChangeForm } from './PasswordChangeForm'

// Nasz herb (StatsBomb id Legii = OUR_TEAM_ID w backendzie) — jak w AppShell.
const OUR_TEAM_ID = 748

/** Pełnoekranowy układ logowania w barwach belki Legia Hub: herb + znak nad kartą formularza. */
function BrandedScreen({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-start justify-center bg-forest-800 px-4 py-12 sm:items-center">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center text-white">
          <TeamCrest teamId={OUR_TEAM_ID} teamName="Legia Warszawa" size={64} />
          <LegiaHubWordmark className="mt-3 text-2xl" />
          <div className="mt-0.5 text-xs text-white/60">Legia Warszawa</div>
          <LangSwitch className="mt-3" />
        </div>
        {children}
      </div>
    </div>
  )
}

export function LoginScreen({ onLoggedIn }: { onLoggedIn: (me: Me) => void }) {
  return (
    <BrandedScreen>
      <LoginForm onLoggedIn={onLoggedIn} subtitle="Legia Hub jest dostępny po zalogowaniu." />
      <p className="mt-4 text-center text-[11px] text-white/60">
        Konta zakłada admin.
      </p>
    </BrandedScreen>
  )
}

/** Konto z hasłem tymczasowym od admina — najpierw własne hasło, potem aplikacja. */
export function ForcedPasswordScreen({ me, onChanged, onLoggedOut }: {
  me: Me
  onChanged: () => void
  onLoggedOut: () => void
}) {
  const logout = () => { authApi.logout().then(onLoggedOut, () => {}) }
  return (
    <BrandedScreen>
      <PasswordChangeForm forced onChanged={onChanged} />
      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-white/60">
        <span>Zalogowano jako {me.user.display_name}.</span>
        <button type="button" onClick={logout} className="font-semibold text-white/80 underline hover:text-white">
          Wyloguj
        </button>
      </div>
    </BrandedScreen>
  )
}

/** /me nie odpowiedziało (np. backend leży) — bez tego nie wiadomo, czy wpuścić. */
export function SessionErrorScreen({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <BrandedScreen>
      <section className="rounded-xl border border-ink-200 bg-white p-4 shadow-card">
        <h2 className="text-sm font-semibold text-ink-900">Nie udało się sprawdzić sesji</h2>
        <p className="mt-1 text-xs text-ink-500">{error}</p>
        <button type="button" onClick={onRetry} className={`${BTN_PRIMARY} mt-3`}>Spróbuj ponownie</button>
      </section>
    </BrandedScreen>
  )
}

/** Zamiast treści strony, do której konto nie ma dostępu. */
export function NoAccess({ landing }: { landing: string | null }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <section className="rounded-xl border border-ink-200 bg-white p-6 shadow-card">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-forest-700">Dostęp</div>
        <h1 className="mt-1 text-lg font-semibold text-ink-900">
          Brak dostępu do tej sekcji — dostęp nadaje admin
        </h1>
        <p className="mt-1 text-sm text-ink-500">
          Sekcje przypisane Twoim rolom (z poziomem Podgląd / Edycja) widać na stronie konta.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {landing && <Link to={landing} className={BTN_PRIMARY}>Przejdź do aplikacji</Link>}
          <Link to="/konto" className={BTN_SECONDARY}>Moje konto</Link>
        </div>
      </section>
    </div>
  )
}

/** Sesja wygasła w trakcie pracy (401 z API) — logowanie NAD stroną, bez jej
 *  odmontowania: niezapisane tagowanie / LIVE zostaje. „Zamknij" odsłania stronę
 *  (np. żeby przepisać notatkę); kolejny 401 otworzy okno ponownie. */
export function ReauthDialog({ onLoggedIn, onClose }: { onLoggedIn: (me: Me) => void; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Ponowne logowanie"
      className="no-print fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 px-4 py-16 backdrop-blur-[2px]"
    >
      <div className="w-full max-w-sm">
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 shadow-card">
          Sesja wygasła. Zaloguj się ponownie — strona i niezapisane zmiany zostają na miejscu.
        </div>
        <LoginForm onLoggedIn={onLoggedIn} />
        <div className="mt-3 text-center">
          <button type="button" onClick={onClose} className="text-xs font-semibold text-white/90 underline hover:text-white">
            Zamknij i zostań na stronie
          </button>
        </div>
      </div>
    </div>
  )
}

export interface ToastMessage {
  id: number
  tone: 'ok' | 'warn'
  text: string
}

const TOAST_TONE: Record<ToastMessage['tone'], string> = {
  ok: 'border-good-200 bg-good-50 text-good-700',
  warn: 'border-amber-300 bg-amber-50 text-amber-900',
}

/** Dyskretny dymek w prawym dolnym rogu; znika sam po kilku sekundach. */
export function Toast({ toast, onClose }: { toast: ToastMessage | null; onClose: () => void }) {
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(onClose, 6000)
    return () => clearTimeout(t)
  }, [toast, onClose])

  if (!toast) return null
  return (
    <div
      role="status"
      className={`no-print fixed bottom-4 right-4 z-[60] flex max-w-sm items-start gap-3 rounded-lg border px-3 py-2 text-sm shadow-card ${TOAST_TONE[toast.tone]}`}
    >
      <span className="min-w-0">{toast.text}</span>
      <button type="button" onClick={onClose} className="font-semibold" aria-label="Zamknij">✕</button>
    </div>
  )
}
